import { supabase } from './supabase';
import { 
  RoomCategory, 
  RoomType, 
  RoomStyle, 
  RoomSize, 
  MoodboardRequest,
  MoodboardWebhookPayload,
  MoodboardGeneration
} from '../types/moodboard';

export const moodboardService = {
  // Get all room categories (Residential/Commercial)
  async getRoomCategories(): Promise<RoomCategory[]> {
    const { data, error } = await supabase
      .from('room_categories')
      .select('*')
      .eq('is_active', true)
      .order('name', { ascending: true });

    if (error) throw error;
    return data || [];
  },

  async addRoomCategory(name: string): Promise<RoomCategory> {
    const { data, error } = await supabase
      .from('room_categories')
      .insert({ name })
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  // Get room types for a specific category
  async getRoomTypes(categoryId: string): Promise<RoomType[]> {
    const { data, error } = await supabase
      .from('room_types')
      .select('*')
      .eq('category_id', categoryId)
      .eq('is_active', true)
      .order('sort_order', { ascending: true });

    if (error) throw error;
    return data || [];
  },

  async getAllRoomTypes(): Promise<RoomType[]> {
    const { data, error } = await supabase.from('room_types').select('*');
    if (error) throw error;
    return data || [];
  },

  async updateRoomType(id: string, name: string, description: string, categoryId: string): Promise<any> {
    const { data, error } = await supabase
      .from('room_types')
      .update({ name, description, category_id: categoryId })
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async addRoomType(name: string, categoryId: string, description: string): Promise<any> {
    const { data, error } = await supabase
      .from('room_types')
      .insert({ name, category_id: categoryId, description })
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  // Get all room styles
  async getRoomStyles(): Promise<RoomStyle[]> {
    const { data, error } = await supabase
      .from('room_styles')
      .select('*')
      .eq('is_active', true)
      .order('sort_order', { ascending: true });

    if (error) throw error;
    return data || [];
  },

  async addRoomStyle(name: string, description: string): Promise<any> {
    const { data, error } = await supabase
      .from('room_styles')
      .insert({ name, description })
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async updateRoomStyle(id: string, name: string, description: string): Promise<any> {
    const { data, error } = await supabase
      .from('room_styles')
      .update({ name, description })
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  // Get all room sizes
  async getRoomSizes(): Promise<RoomSize[]> {
    const { data, error } = await supabase
      .from('room_sizes')
      .select('*')
      .eq('is_active', true)
      .order('sort_order', { ascending: true });

    if (error) throw error;
    return data || [];
  },

  async addRoomSize(name: string): Promise<any> {
    const { data, error } = await supabase
      .from('room_sizes')
      .insert({ name })
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  // Get user's moodboard generations
  async getMoodboardGenerations(userId: string): Promise<MoodboardGeneration[]> {
    const { data, error } = await supabase
      .from('moodboard_generations')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });
    if (error) throw error;
    return data || [];
  },

  // Get moodboard generations with all associated images
  async getMoodboardGenerationsWithImages(userId: string): Promise<(MoodboardGeneration & { images?: any[] })[]> {
    const generations = await this.getMoodboardGenerations(userId);
    console.log(`Found ${generations.length} generations for user ${userId}`);
    
    // For each generation, fetch its images
    const generationsWithImages = await Promise.all(
      generations.map(async (generation) => {
        try {
          const images = await this.getGenerationImages(generation.id);
          console.log(`Generation ${generation.id}: found ${images.length} additional images`);
          return {
            ...generation,
            images: images.length > 0 ? images : undefined
          };
        } catch (error) {
          console.warn(`Failed to fetch images for generation ${generation.id}:`, error);
          return generation;
        }
      })
    );

    return generationsWithImages;
  },

  // Debug function to check database setup
  async checkMultipleImagesSupport(): Promise<{ supported: boolean; message: string }> {
    try {
      // Try to query the moodboard_generation_images table
      const { error } = await supabase
        .from('moodboard_generation_images')
        .select('id')
        .limit(1);
      
      if (error) {
        return {
          supported: false,
          message: `Multiple images table not found: ${error.message}`
        };
      }
      
      return {
        supported: true,
        message: 'Multiple images support is properly configured'
      };
    } catch (error) {
      return {
        supported: false,
        message: `Error checking multiple images support: ${error}`
      };
    }
  },
  
  async createGeneration(
    userId: string, 
    options: Partial<MoodboardGeneration>
  ): Promise<MoodboardGeneration> {
    const { data, error } = await supabase
      .from('moodboard_generations')
      .insert({ user_id: userId, ...options })
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  async markGenerationCompleted(
    processingId: string, 
    resultImageUrl: string
  ): Promise<MoodboardGeneration> {
    const { data, error } = await supabase
      .from('moodboard_generations')
      .update({ result_image_url: resultImageUrl, status: 'completed', updated_at: new Date().toISOString() })
      .eq('processing_id', processingId)
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  // Store multiple images for a generation using the database function
  async storeMultipleImages(
    generationId: string,
    imageUrls: string[],
    imageNames?: string[]
  ): Promise<number> {
    const { data, error } = await supabase.rpc('store_generation_images', {
      p_generation_id: generationId,
      p_image_urls: imageUrls,
      p_image_names: imageNames || null
    });
    
    if (error) throw error;
    return data;
  },

  // Get all images for a specific generation
  async getGenerationImages(generationId: string): Promise<any[]> {
    const { data, error } = await supabase
      .from('moodboard_generation_images')
      .select('*')
      .eq('generation_id', generationId)
      .order('sort_order');
    
    if (error) {
      console.warn('Error fetching generation images (table might not exist):', error);
      return [];
    }
    return data || [];
  },

  // Complete a generation with multiple images (handles both single and multiple)
  async markGenerationCompletedWithMultipleImages(
    processingId: string,
    imageUrls: string[]
  ): Promise<MoodboardGeneration> {
    if (imageUrls.length === 0) {
      throw new Error('No images provided');
    }

    // First, get the generation by processing_id to get the generation ID
    const { data: generation, error: getError } = await supabase
      .from('moodboard_generations')
      .select('id')
      .eq('processing_id', processingId)
      .single();

    if (getError) throw getError;

    // If multiple images, use the store_generation_images function
    if (imageUrls.length > 1) {
      await this.storeMultipleImages(generation.id, imageUrls);
    } else {
      // For single image, use the traditional approach
      await this.markGenerationCompleted(processingId, imageUrls[0]);
    }

    // Return the updated generation
    const { data: updatedGeneration, error: updateError } = await supabase
      .from('moodboard_generations')
      .select('*')
      .eq('processing_id', processingId)
      .single();

    if (updateError) throw updateError;
    return updatedGeneration;
  },

  // Helper function to parse n8n moodboard response
  parseN8NMoodboardResponse(
    n8nResponse: any, 
    imageBaseUrl: string = process.env.REACT_APP_IMAGE_BASE_URL || 'https://your-image-server.com/images/'
  ): string[] {
    let imageUrls: string[] = [];
    
    // Parse the new n8n response format: [{ "data": [{"image_name": "..."}, ...] }, ...]
    if (n8nResponse && Array.isArray(n8nResponse) && n8nResponse[0]?.data && Array.isArray(n8nResponse[0].data)) {
      imageUrls = n8nResponse[0].data
        .filter((item: any) => item.image_name)
        .map((item: any) => `${imageBaseUrl}${item.image_name}`);
    }
    
    // Fallback to legacy format if new format is not found
    if (imageUrls.length === 0) {
      const legacyImageUrl = n8nResponse[0]?.response?.body[0]?.fullUrl || 
                            n8nResponse[0]?.fullUrl || 
                            n8nResponse.image || 
                            n8nResponse.fullUrl;
      if (legacyImageUrl) {
        imageUrls = [legacyImageUrl];
      }
    }
    
    return imageUrls;
  },

  async markGenerationFailed(
    processingId: string, 
    errorMessage: string
  ): Promise<MoodboardGeneration> {
    const { data, error } = await supabase
      .from('moodboard_generations')
      .update({ error_message: errorMessage, status: 'failed', updated_at: new Date().toISOString() })
      .eq('processing_id', processingId)
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  // Create a moodboard request
  async createMoodboardRequest(
    userId: string,
    categoryId: string,
    roomTypeId: string,
    roomStyleId: string,
    roomSizeId: string
  ): Promise<MoodboardRequest> {
    const { data, error } = await supabase
      .from('moodboard_requests')
      .insert({
        user_id: userId,
        category_id: categoryId,
        room_type_id: roomTypeId,
        room_style_id: roomStyleId,
        room_size_id: roomSizeId,
        webhook_sent: false,
        tokens_spent: 0
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  // Get user's moodboard request history
  async getUserMoodboardRequests(userId: string): Promise<MoodboardRequest[]> {
    const { data, error } = await supabase
      .from('moodboard_requests')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  },

  // Send request to N8N webhook
  async sendToN8NWebhook(payload: MoodboardWebhookPayload): Promise<string> {
    const webhookUrl = process.env.REACT_APP_N8N_WEBHOOK_URL_MOODBOARD;
    
    if (!webhookUrl || webhookUrl === 'your_n8n_webhook_url_here') {
      if (process.env.NODE_ENV === 'development') {
        console.warn('⚠️ N8N webhook URL not configured');
      }
      return 'Webhook not configured - this would be sent to N8N in production';
    }

    try {
      const response = await fetch(webhookUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.text();
      return result;
    } catch (error) {
      console.error('❌ Error sending to N8N webhook:', error);
      throw error;
    }
  },

  // Update moodboard request with webhook response
  async updateMoodboardRequestWebhook(
    requestId: string, 
    webhookResponse: string, 
    webhookSent: boolean = true
  ): Promise<void> {
    const { error } = await supabase
      .from('moodboard_requests')
      .update({
        webhook_sent: webhookSent,
        webhook_response: webhookResponse,
        updated_at: new Date().toISOString()
      })
      .eq('id', requestId);

    if (error) throw error;
  },

  // Process complete moodboard request (create + send webhook)
  async processMoodboardRequest(
    userId: string,
    categoryId: string,
    roomTypeId: string,
    roomStyleId: string,
    roomSizeId: string,
    selections: {
      category: string;
      roomType: string;
      roomStyle: string;
      roomSize: string;
    }
  ): Promise<{ request: MoodboardRequest; webhookResponse: string }> {
    // Create the request in database
    const request = await this.createMoodboardRequest(
      userId,
      categoryId,
      roomTypeId,
      roomStyleId,
      roomSizeId
    );

    // Prepare webhook payload
    const webhookPayload: MoodboardWebhookPayload = {
      user_id: userId,
      category: selections.category,
      room_type: selections.roomType,
      room_style: selections.roomStyle,
      room_size: selections.roomSize,
      request_id: request.id
    };

    try {
      // Send to N8N webhook
      const webhookResponse = await this.sendToN8NWebhook(webhookPayload);
      
      // Update request with webhook response
      await this.updateMoodboardRequestWebhook(request.id, webhookResponse, true);

      return { request, webhookResponse };
    } catch (error) {
      // Update request with error
      await this.updateMoodboardRequestWebhook(
        request.id, 
        `Error: ${error instanceof Error ? error.message : 'Unknown error'}`, 
        false
      );
      throw error;
    }
  }
}; 